import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import StatCard from '../dashboard/components/StatCard';
import TurnoverRateChart from './components/TurnoverRateChart';
import UsagePatternChart from './components/UsagePatternChart';
import ReorderRecommendations from './components/ReorderRecommendations';
import CategoryAnalytics from './components/CategoryAnalytics';
import Button from '../../components/ui/Button';

const InventoryAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [dateRange, setDateRange] = useState('30');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch products and calculate analytics from base tables
      const { data: productsData, error: productsError } = await supabase
        ?.from('products')
        ?.select('*')
        ?.eq('is_trackable', true)
        ?.order('name', { ascending: true });

      if (productsError) throw productsError;

      // Fetch transactions for the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      
      const { data: transactionsData, error: transactionsError } = await supabase
        ?.from('inventory_transactions')
        ?.select('*')
        ?.gte('created_at', thirtyDaysAgo?.toISOString());

      if (transactionsError) throw transactionsError;

      // Calculate analytics for each product
      const analyticsViewData = productsData?.map(product => {
        const productTransactions = transactionsData?.filter(t => t?.product_id === product?.id) || [];
        
        const stockOut30d = productTransactions
          ?.filter(t => t?.transaction_type === 'stock_out' || t?.transaction_type === 'transfer')
          ?.reduce((sum, t) => sum + (t?.quantity || 0), 0);
        
        const stockIn30d = productTransactions
          ?.filter(t => t?.transaction_type === 'stock_in')
          ?.reduce((sum, t) => sum + (t?.quantity || 0), 0);
        
        const transactionCount30d = productTransactions?.length || 0;
        const avgDailyUsage = stockOut30d / 30;
        
        const daysUntilReorder = avgDailyUsage > 0 
          ? Math.round((product?.quantity - product?.reorder_level) / avgDailyUsage)
          : null;

        return {
          ...product,
          stock_out_30d: stockOut30d,
          stock_in_30d: stockIn30d,
          transaction_count_30d: transactionCount30d,
          avg_daily_usage: avgDailyUsage,
          days_until_reorder: daysUntilReorder
        };
      }) || [];

      setAnalyticsData(analyticsViewData || []);

      // Fetch reorder recommendations using RPC
      const { data: recommendationsData, error: recommendationsError } = await supabase
        ?.rpc('get_reorder_recommendations');

      if (recommendationsError) {
        console.warn('Recommendations RPC error:', recommendationsError);
        // Generate recommendations from analytics data if RPC fails
        const fallbackRecommendations = analyticsViewData
          ?.filter(p => p?.status === 'Low Stock' || p?.status === 'Out of Stock' || p?.quantity <= p?.reorder_level)
          ?.map(p => ({
            product_id: p?.id,
            product_name: p?.name,
            sku: p?.sku,
            category: p?.category,
            current_quantity: p?.quantity,
            reorder_level: p?.reorder_level,
            avg_daily_usage: p?.avg_daily_usage,
            recommended_order_qty: Math.max(p?.reorder_level * 2, Math.ceil(p?.avg_daily_usage * 30)),
            days_until_stockout: p?.avg_daily_usage > 0 ? Math.floor(p?.quantity / p?.avg_daily_usage) : 999,
            priority: p?.status === 'Out of Stock' ? 'Critical' : p?.status === 'Low Stock' && p?.quantity <= p?.reorder_level * 0.5 ? 'High' : p?.status === 'Low Stock' ? 'Medium' : 'Low',
            confidence_score: Math.min(100, p?.transaction_count_30d * 10)
          }))
          ?.sort((a, b) => {
            const priorityOrder = { 'Critical': 1, 'High': 2, 'Medium': 3, 'Low': 4 };
            return priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority];
          });
        setRecommendations(fallbackRecommendations || []);
      } else {
        setRecommendations(recommendationsData || []);
      }

      // Fetch category analytics using RPC
      const { data: categoryAnalyticsData, error: categoryError } = await supabase
        ?.rpc('get_category_analytics');

      if (categoryError) {
        console.warn('Category analytics RPC error:', categoryError);
        // Generate category analytics from products data if RPC fails
        const categoryMap = {};
        analyticsViewData?.forEach(product => {
          const cat = product?.category || 'Uncategorized';
          if (!categoryMap?.[cat]) {
            categoryMap[cat] = {
              category: cat,
              total_products: 0,
              total_value: 0,
              avg_turnover_rate: 0,
              low_stock_count: 0,
              out_of_stock_count: 0,
              turnover_sum: 0
            };
          }
          categoryMap[cat].total_products += 1;
          categoryMap[cat].total_value += (product?.quantity || 0) * (product?.cost || 0);
          const turnover = product?.quantity > 0 ? (product?.stock_out_30d || 0) / product?.quantity : 0;
          categoryMap[cat].turnover_sum += turnover;
          if (product?.status === 'Low Stock') categoryMap[cat].low_stock_count += 1;
          if (product?.status === 'Out of Stock') categoryMap[cat].out_of_stock_count += 1;
        });
        
        const fallbackCategoryData = Object.values(categoryMap)?.map(cat => ({
          ...cat,
          avg_turnover_rate: cat?.total_products > 0 ? (cat?.turnover_sum / cat?.total_products)?.toFixed(2) : '0.00'
        }));
        setCategoryData(fallbackCategoryData || []);
      } else {
        setCategoryData(categoryAnalyticsData || []);
      }

      // Extract unique categories
      const uniqueCategories = [...new Set(analyticsViewData?.map(item => item?.category) || [])];
      setCategories(uniqueCategories);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();

    // Real-time subscription for products and transactions
    const productsChannel = supabase
      ?.channel('analytics_products_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'products'
        },
        () => {
          fetchAnalyticsData();
        }
      )
      ?.subscribe();

    const transactionsChannel = supabase
      ?.channel('analytics_transactions_changes')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'inventory_transactions'
        },
        () => {
          fetchAnalyticsData();
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(productsChannel);
      supabase?.removeChannel(transactionsChannel);
    };
  }, []);

  // Filter data by category
  const filteredAnalyticsData = selectedCategory === 'all'
    ? analyticsData
    : analyticsData?.filter(item => item?.category === selectedCategory);

  // Calculate KPI statistics
  const stats = {
    totalProducts: filteredAnalyticsData?.length || 0,
    avgTurnoverRate: filteredAnalyticsData?.length > 0
      ? (filteredAnalyticsData?.reduce((sum, item) => {
          const stockOut = parseFloat(item?.stock_out_30d) || 0;
          const avgQty = parseFloat(item?.quantity) || 1;
          return sum + (stockOut / avgQty);
        }, 0) / filteredAnalyticsData?.length)?.toFixed(2)
      : '0.00',
    criticalReorders: recommendations?.filter(r => r?.priority === 'Critical')?.length || 0,
    totalInventoryValue: filteredAnalyticsData?.reduce((sum, item) => {
      return sum + (parseFloat(item?.quantity) || 0) * (parseFloat(item?.cost) || 0);
    }, 0)?.toFixed(2)
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading analytics data...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-heading font-bold text-foreground">
              Inventory Analytics Dashboard
            </h1>
            <Button
              onClick={fetchAnalyticsData}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Icon name="RefreshCw" size={16} />
              Refresh
            </Button>
          </div>
          <p className="text-muted-foreground">
            Comprehensive data-driven insights into inventory performance and optimization opportunities
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
            <p className="text-destructive font-medium">{error}</p>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground">Date Range:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-3 py-2 bg-card border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="60">Last 60 Days</option>
              <option value="90">Last 90 Days</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground">Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="px-3 py-2 bg-card border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Categories</option>
              {categories?.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <StatCard
            title="Total Products Tracked"
            value={stats?.totalProducts}
            icon="Package"
            iconColor="var(--color-primary)"
            subtitle="Trackable inventory items"
            trend=""
            trendDirection="up"
            onClick={() => {}}
          />
          <StatCard
            title="Avg Turnover Rate"
            value={stats?.avgTurnoverRate}
            icon="TrendingUp"
            iconColor="var(--color-success)"
            subtitle="Times per 30 days"
            trend={stats?.avgTurnoverRate > 1 ? 'Healthy turnover' : 'Low turnover'}
            trendDirection="up"
            onClick={() => {}}
          />
          <StatCard
            title="Critical Reorders"
            value={stats?.criticalReorders}
            icon="AlertTriangle"
            iconColor="var(--color-error)"
            subtitle="Require immediate action"
            trend={stats?.criticalReorders > 0 ? 'Action required' : 'All good'}
            trendDirection={stats?.criticalReorders > 0 ? 'down' : 'up'}
            onClick={() => {}}
          />
          <StatCard
            title="Total Inventory Value"
            value={`${parseFloat(stats?.totalInventoryValue)?.toLocaleString()}`}
            icon="DollarSign"
            iconColor="var(--color-info)"
            subtitle="Current stock value"
            trend=""
            trendDirection="up"
            onClick={() => {}}
          />
        </div>

        {/* Charts and Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <TurnoverRateChart data={filteredAnalyticsData} dateRange={dateRange} />
          <CategoryAnalytics data={categoryData} />
        </div>

        {/* Usage Patterns */}
        <div className="mb-6">
          <UsagePatternChart analyticsData={filteredAnalyticsData} dateRange={dateRange} />
        </div>

        {/* Reorder Recommendations */}
        <div>
          <ReorderRecommendations recommendations={recommendations} onRefresh={fetchAnalyticsData} />
        </div>
      </div>
    </>
  );
};

export default InventoryAnalyticsDashboard;